import React from 'react';
import { motion } from 'framer-motion';
import { BadgeSlot } from './BadgeSlot';
import { type BadgeSlotModel } from '../../../utils/adventurePresentation';

interface AdventureTaskCardProps {
  title: string;
  story: string;
  progressLabel: string;
  badgeSlots: BadgeSlotModel[];
  imageSrc: string;
  onPreviewImage: () => void;
  onContinue: () => void;
  onViewBadges: () => void;
}

export const AdventureTaskCard: React.FC<AdventureTaskCardProps> = ({
  title,
  story,
  progressLabel,
  badgeSlots,
  imageSrc,
  onPreviewImage,
  onContinue,
  onViewBadges,
}) => (
  <motion.article
    whileHover={{ y: -4, scale: 1.01 }}
    whileTap={{ scale: 0.99 }}
    className="overflow-hidden rounded-[28px] border border-emerald-100 bg-[linear-gradient(135deg,#f0fdf4,#ffffff_58%,#ecfeff)] p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)]"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="max-w-[70%]">
        <p className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
          冒险任务
        </p>
        <h3 className="mt-3 text-2xl font-black text-slate-900">{title}</h3>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">{story}</p>
      </div>
      <div className="flex flex-col items-end gap-3">
        <div className="rounded-2xl bg-white/90 px-4 py-3 text-right shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">当前进度</p>
          <p className="mt-1 text-lg font-black text-slate-900">{progressLabel}</p>
        </div>
        <button
          type="button"
          onClick={onPreviewImage}
          aria-label={`预览${title}任务图片`}
          className="group relative overflow-hidden rounded-[22px] border border-white/80 bg-white shadow-[0_14px_32px_rgba(15,23,42,0.14)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(15,23,42,0.18)]"
        >
          <img
            src={imageSrc}
            alt={`${title}任务缩略图`}
            loading="lazy"
            className="h-24 w-24 object-cover sm:h-28 sm:w-28"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/65 via-slate-900/10 to-transparent px-3 py-2 text-left text-xs font-semibold text-white">
            点击查看大图
          </div>
        </button>
      </div>
    </div>

    <div className="mt-5 flex items-center gap-3">
      {badgeSlots.map((badge) => (
        <BadgeSlot key={badge.id} badge={badge} testId="dashboard-badge-slot" />
      ))}
    </div>

    <div className="mt-5 flex flex-col gap-3 sm:flex-row">
      <button
        type="button"
        onClick={onContinue}
        className="min-h-touch flex-1 rounded-full bg-emerald-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-600"
      >
        继续冒险
      </button>
      <button
        type="button"
        onClick={onViewBadges}
        className="min-h-touch flex-1 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50"
      >
        查看我的勋章墙
      </button>
    </div>
  </motion.article>
);
